import React from 'react';
import { clienteleData } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Building2, Factory, Zap, Hammer, Globe } from 'lucide-react';

const ClienteleSection = () => {
  const iconMap = {
    'Power Industries': Zap,
    'Steel Industries': Hammer,
    'Palm Oil Mills': Globe,
    'Cement Industries': Building2,
    'Other Industries': Factory
  };

  return (
    <section id="clientele" className="py-10 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#cd2d31]/10 border border-[#cd2d31]/20 rounded-full px-4 py-2 mb-6">
            <span className="text-[#cd2d31] font-semibold text-sm">Our Partners</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {clienteleData.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {clienteleData.subtitle}
          </p>
        </div>

        {/* Industry Tabs */}
        <Tabs defaultValue="Power Industries" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-12 bg-gray-100 p-1 rounded-lg">
            {clienteleData.categories.map((category) => {
              const IconComponent = iconMap[category.name];
              return (
                <TabsTrigger 
                  key={category.name}
                  value={category.name}
                  className="flex items-center space-x-2 data-[state=active]:bg-[#0a8ec5] data-[state=active]:text-white transition-all duration-200"
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="hidden sm:inline">{category.name.split(' ')[0]}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {clienteleData.categories.map((category) => (
            <TabsContent key={category.name} value={category.name} className="mt-0">
              <div className="bg-gradient-to-br from-gray-50/50 to-white rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
                    {React.createElement(iconMap[category.name], { className: "w-7 h-7 text-[#0a8ec5]" })}
                    <span>{category.name}</span>
                  </h3>
                  <Badge variant="outline" className="border-[#cd2d31] text-[#cd2d31]">
                    {category.clients.length} Clients
                  </Badge>
                </div>
                
                {/* Clients Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {category.clients.map((client, index) => (
                    <div 
                      key={index}
                      className="group bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-200"
                    >
                      {/* Client Logo Placeholder
                      <div className="w-full h-16 bg-gradient-to-br from-[#0a8ec5]/10 to-[#cd2d31]/10 rounded-lg flex items-center justify-center mb-4">
                       */}<div className="w-full h-20 flex items-center justify-center mb-4">
                        {/* <span className="text-[#0a8ec5] font-bold text-xs text-center px-2">
                          {client.name}
                        </span> */}
                        <img 
                          src={client.logo} 
                          alt={`${client.name} logo`}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      
                      {/* Client Name */}
                      <h4 className="text-sm font-semibold text-gray-900 text-center group-hover:text-[#0a8ec5] transition-colors">
                        {client.name}
                      </h4>
                    </div>
                  ))}
                </div>

                {/* Industry Stats */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#0a8ec5] mb-1">
                        {Math.floor(Math.random() * 50) + 20}+
                      </div>
                      <div className="text-gray-600 text-sm">Projects Delivered</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#cd2d31] mb-1">
                        {Math.floor(Math.random() * 20) + 5}+
                      </div>
                      <div className="text-gray-600 text-sm">Years Partnership</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#0a8ec5] mb-1">99.9%</div>
                      <div className="text-gray-600 text-sm">Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Trust Indicators */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-[#0a8ec5]/5 to-[#cd2d31]/5 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Industry Leaders Choose Us</h3>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0a8ec5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-[#0a8ec5]" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Enterprise Grade</h4>
                <p className="text-gray-600 text-sm">Solutions designed for large-scale industrial operations</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#cd2d31]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-[#cd2d31]" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Proven Results</h4>
                <p className="text-gray-600 text-sm">Consistently achieving 99.9% efficiency across projects</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0a8ec5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-[#0a8ec5]" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Global Reach</h4>
                <p className="text-gray-600 text-sm">Successfully serving clients across 15+ countries</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#cd2d31]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory className="w-8 h-8 text-[#cd2d31]" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Industry Expertise</h4>
                <p className="text-gray-600 text-sm">Deep understanding of diverse industrial processes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClienteleSection;